/**
 * Account Limits Panel (Schritt 4)
 *
 * Tageslimits sind eine ACCOUNT-Einstellung: wie viele Kontaktanfragen,
 * Nachrichten, InMails und Profilbesuche der Account pro Tag maximal
 * ausführt. Der Step-Runner verbraucht dieses Kontingent (DailyQuota) über
 * alle Kampagnen des Accounts hinweg. Konservative Werte schützen den
 * Account vor LinkedIn-Einschränkungen.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, MenuItem, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { getAllLinkedinAccounts } from '../../../API/linkedin-accounts';
import { patchLinkedinAccountLimits } from '../../../API/linkedin-account-limits';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';
import { DAILY_LIMIT_MAXIMUMS, DAILY_LIMIT_RECOMMENDED } from '../../../constants/linkedin-daily-limits';

interface AccountLimitsPanelProps {
  onSaved: (summary: string) => void;
  /** Nach dem Speichern weiter zum nächsten Schritt. */
  onComplete?: () => void;
}

interface AccountRow {
  id: string;
  label: string;
  limits: LimitValues;
}

interface LimitValues {
  connections: number;
  messages: number;
  inmails: number;
  visits: number;
}

/** Empfohlene Werte und harte Obergrenzen aus constants/linkedin-daily-limits.ts — dieselben wie im Konto-Dialog. */
const LIMIT_META: Array<{
  key: keyof LimitValues;
  de: string;
  en: string;
  safe: number;
  max: number;
}> = [
  { key: 'connections', de: 'Kontaktanfragen / Tag', en: 'Connection requests / day', safe: DAILY_LIMIT_RECOMMENDED.connections, max: DAILY_LIMIT_MAXIMUMS.connections },
  { key: 'messages', de: 'Nachrichten / Tag', en: 'Messages / day', safe: DAILY_LIMIT_RECOMMENDED.messages, max: DAILY_LIMIT_MAXIMUMS.messages },
  { key: 'inmails', de: 'InMails / Tag', en: 'InMails / day', safe: DAILY_LIMIT_RECOMMENDED.inmails, max: DAILY_LIMIT_MAXIMUMS.inmails },
  { key: 'visits', de: 'Profilbesuche / Tag', en: 'Profile visits / day', safe: DAILY_LIMIT_RECOMMENDED.visits, max: DAILY_LIMIT_MAXIMUMS.visits },
];

const AccountLimitsPanel: React.FC<AccountLimitsPanelProps> = ({ onSaved, onComplete }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const dataVersion = useAIChatStore((state) => state.dataVersion);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState('');
  const [values, setValues] = useState<LimitValues>({ connections: 20, messages: 50, inmails: 20, visits: 250 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAllLinkedinAccounts();
        if (cancelled) return;
        const rows: AccountRow[] = (res?.allLinkedInAccounts || []).map((a: any) => ({
          id: a._id,
          label: [a.firstName, a.lastName].filter(Boolean).join(' ') || a.linkedinEmail || a._id,
          limits: {
            connections: a.dailyLimits?.connections ?? 20,
            messages: a.dailyLimits?.messages ?? 50,
            // Altwerte über dem Maximum (früher Standard 30) gekürzt anzeigen, sonst ließe sich nichts speichern.
            inmails: Math.min(a.dailyLimits?.inmails ?? 20, 25),
            visits: a.dailyLimits?.visits ?? 150,
          },
        }));
        setAccounts(rows);
        const current = rows.find((r) => r.id === accountId) || rows[0];
        if (current) {
          setAccountId(current.id);
          setValues(current.limits);
        }
      } catch {
        if (!cancelled) setError(isDe ? 'Accounts konnten nicht geladen werden.' : 'Could not load accounts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const overSafe = LIMIT_META.filter((m) => values[m.key] > m.safe);
  const canSave = !!accountId && LIMIT_META.every((m) => values[m.key] >= 0 && values[m.key] <= m.max) && !saving;
  const currentAccount = accounts.find((a) => a.id === accountId);

  const handleSave = async () => {
    if (!canSave || !currentAccount) return;
    setSaving(true);
    setError(null);
    try {
      const res: any = await patchLinkedinAccountLimits(accountId, {
        connectionDailyLimit: values.connections,
        messageDailyLimit: values.messages,
        inmailDailyLimit: values.inmails,
        visitDailyLimit: values.visits,
      });
      if (res?.error) throw new Error('save failed');
      setAccounts((prev) => prev.map((a) => (a.id === accountId ? { ...a, limits: { ...values } } : a)));
      setSaved(true);
      onSaved(
        isDe
          ? `Ich habe die Tageslimits für den LinkedIn-Account „${currentAccount.label}" im Formular gespeichert: ${values.connections} Kontaktanfragen, ${values.messages} Nachrichten, ${values.inmails} InMails, ${values.visits} Profilbesuche pro Tag. Bitte bestätige kurz und schlage mir die nächsten Schritte vor.`
          : `I just saved the daily limits for the LinkedIn account "${currentAccount.label}": ${values.connections} connection requests, ${values.messages} messages, ${values.inmails} InMails, ${values.visits} profile visits per day. Please confirm briefly and suggest my next steps.`
      );
      onComplete?.();
    } catch {
      setError(isDe ? 'Speichern fehlgeschlagen — bitte erneut versuchen.' : 'Saving failed — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (accounts.length === 0) {
    return (
      <Box className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {isDe
            ? 'Noch kein LinkedIn-Account verbunden — schließe zuerst Schritt 2 ab.'
            : 'No LinkedIn account connected yet — complete step 2 first.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {overSafe.length > 0 ? (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {isDe
              ? `Über der Empfehlung: ${overSafe.map((m) => m.de).join(', ')} — hohe Limits erhöhen das Risiko von LinkedIn-Einschränkungen.`
              : `Above recommendation: ${overSafe.map((m) => m.en).join(', ')} — high limits increase the risk of LinkedIn restrictions.`}
          </Typography>
        </Box>
      ) : (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {saved
              ? isDe ? 'Limits gespeichert' : 'Limits saved'
              : isDe ? 'Limits im sicheren Bereich' : 'Limits within the safe range'}
          </Typography>
        </Box>
      )}

      {accounts.length > 1 && (
        <Box className={classes.card}>
          <Typography variant="caption" className={classes.sectionLabel}>
            {isDe ? 'LinkedIn-Account' : 'LinkedIn account'}
          </Typography>
          <TextField
            size="small"
            fullWidth
            select
            value={accountId}
            onChange={(e) => {
              const row = accounts.find((a) => a.id === e.target.value);
              if (row) {
                setAccountId(row.id);
                setValues(row.limits);
                setSaved(false);
              }
            }}
            helperText={
              isDe
                ? 'Limits gelten pro Account — das Tageskontingent teilen sich alle seine Kampagnen.'
                : 'Limits apply per account — all of its campaigns share the daily quota.'
            }
          >
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Box className={classes.card}>
        <Typography variant="caption" className={classes.sectionLabel}>
          {isDe ? 'Maximal-Limits pro Tag' : 'Maximum limits per day'}
        </Typography>
        <Box className={classes.grid}>
          {LIMIT_META.map((m) => (
            <TextField
              key={m.key}
              size="small"
              fullWidth
              type="number"
              label={isDe ? m.de : m.en}
              value={values[m.key]}
              onChange={(e) => {
                const n = Math.max(0, Math.min(m.max, Number(e.target.value) || 0));
                setValues((prev) => ({ ...prev, [m.key]: n }));
                setSaved(false);
              }}
              inputProps={{ min: 0, max: m.max }}
              error={values[m.key] > m.safe}
              helperText={
                isDe
                  ? `Empfohlen: ≤ ${m.safe} · Maximum: ${m.max}`
                  : `Recommended: ≤ ${m.safe} · Maximum: ${m.max}`
              }
            />
          ))}
        </Box>
        <Typography variant="caption" color="textSecondary">
          {isDe
            ? 'So funktioniert es: Das ist das Tageskontingent des Accounts. Alle Kampagnen dieses Accounts teilen es sich — ist z.B. das Nachrichten-Limit erreicht, pausiert der Agent bis zum nächsten Arbeitstag.'
            : 'How it works: this is the account’s daily quota. All campaigns of this account share it — e.g. once the message limit is reached, the agent pauses until the next working day.'}
        </Typography>
      </Box>

      {error && (
        <Typography variant="caption" className={classes.error}>
          {error}
        </Typography>
      )}

      <Box className={classes.footer}>
        <Typography variant="caption" className={classes.footerHint}>
          {saved
            ? isDe
              ? 'Gespeichert — der Assistent meldet sich im Chat.'
              : 'Saved — the assistant will follow up in the chat.'
            : isDe
              ? 'Konservative Limits schützen deinen Account.'
              : 'Conservative limits protect your account.'}
        </Typography>
        <button type="button" className={classes.saveButton} onClick={handleSave} disabled={!canSave}>
          {saving ? (isDe ? 'Speichert…' : 'Saving…') : isDe ? 'Weiter' : 'Next'}
        </button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  gapBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: dc.warn,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: dc.warnText,
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: dc.success,
  },
  doneText: {
    color: '#0B7050',
  },
  card: {
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.muted,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: dc.muted,
    lineHeight: 1.4,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.primary,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default AccountLimitsPanel;
